/**
 * Debug logging utilities
 * 
 * These utilities help with conditional logging that should only be visible
 * to authorized users like superusers/admins.
 */

interface User {
  is_superuser?: boolean;
  [key: string]: any;
}

/**
 * Supported log levels
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Logs messages of different levels for superusers
 * 
 * @param user The current user object (used to check superuser status)
 * @param level The log level (debug, info, warn, error)
 * @param source The source/component name generating the log
 * @param message The message to log
 * @param data Optional data to include in the log
 */
export const logMessage = (
  user: User | null | undefined,
  level: LogLevel,
  source: string,
  message: string,
  data?: any
): void => {
  // Only log if the user is a superuser
  if (user?.is_superuser) {
    // Format the prefix consistently
    const prefix = `[${source}]`;
    
    // Use the appropriate console method based on level
    switch (level) {
      case 'debug':
        console.log(prefix, message, data || '');
        break;
      case 'info':
        console.info(prefix, message, data || '');
        break;
      case 'warn':
        console.warn(prefix, message, data || '');
        break;
      case 'error':
        console.error(prefix, message, data || '');
        break;
      default:
        console.log(prefix, message, data || '');
    }
  }
};

/**
 * Logs debug messages only for superusers
 * 
 * @param user The current user object (used to check superuser status)
 * @param source The source/component name generating the log
 * @param message The message to log
 * @param data Optional data to include in the log
 */
export const logDebug = (
  user: User | null | undefined,
  source: string,
  message: string,
  data?: any
): void => {
  logMessage(user, 'debug', source, message, data);
};

/**
 * Logs info messages only for superusers
 * 
 * @param user The current user object (used to check superuser status)
 * @param source The source/component name generating the log
 * @param message The message to log
 * @param data Optional data to include in the log
 */
export const logInfo = (
  user: User | null | undefined,
  source: string,
  message: string,
  data?: any
): void => {
  logMessage(user, 'info', source, message, data);
};

/**
 * Logs warning messages only for superusers
 * 
 * @param user The current user object (used to check superuser status)
 * @param source The source/component name generating the log
 * @param message The message to log
 * @param data Optional data to include in the log
 */
export const logWarn = (
  user: User | null | undefined,
  source: string,
  message: string,
  data?: any
): void => {
  logMessage(user, 'warn', source, message, data);
};

/**
 * Logs error messages only for superusers
 * 
 * @param user The current user object (used to check superuser status)
 * @param source The source/component name generating the log
 * @param message The message to log
 * @param data Optional data to include in the log
 */
export const logError = (
  user: User | null | undefined,
  source: string,
  message: string,
  data?: any
): void => {
  logMessage(user, 'error', source, message, data);
};

/**
 * Creates a scoped logger function that's bound to a specific user and source
 * 
 * @param user The current user object (used to check superuser status)
 * @param source The source/component name for all logs from this logger
 * @returns Object with methods for different log levels
 */
export const createScopedLogger = (
  user: User | null | undefined,
  source: string
) => {
  return {
    debug: (message: string, data?: any) => logDebug(user, source, message, data),
    info: (message: string, data?: any) => logInfo(user, source, message, data),
    warn: (message: string, data?: any) => logWarn(user, source, message, data),
    error: (message: string, data?: any) => logError(user, source, message, data),
  };
};